import {
    daysBetween,
    DeadlineMilestoneId,
    isLaunchMilestone,
    MAX_DAYS_ALLOWED_TO_COMPLETE,
    type DeadlineMilestoneDefinition,
    type DeadlineMilestoneIdValue,
} from './milestones';

import type { FrequencyValue } from '@/constants/Frequency';
import type { TranslationKey } from '@/i18n-key';

export interface DeadlineMilestoneValue {
    scheduledAt?: Date;
    dueAt?: Date;
}

export type DeadlineMilestoneValues = Partial<
    Record<DeadlineMilestoneIdValue, DeadlineMilestoneValue>
>;

export interface DeadlineFieldError {
    key: TranslationKey;
    severity: 'blocking' | 'warning';
}

export interface DeadlineMilestoneErrors {
    scheduledAt?: DeadlineFieldError;
    dueAt?: DeadlineFieldError;
}

export type DeadlineErrorsById = Partial<Record<DeadlineMilestoneIdValue, DeadlineMilestoneErrors>>;

const isDateInPast = ({ date, now }: { date: Date; now: Date }): boolean =>
    date.getTime() < now.getTime();

const getEffectiveLastDate = ({
    milestone,
    value,
}: {
    milestone: DeadlineMilestoneDefinition;
    value: DeadlineMilestoneValue | undefined;
}): Date | undefined =>
    isLaunchMilestone(milestone) ? (value?.dueAt ?? value?.scheduledAt) : value?.scheduledAt;

const DEADLINE_FIELD_SEVERITY_RANK: Record<DeadlineFieldError['severity'], number> = {
    warning: 0,
    blocking: 1,
};

/**
 * A date already gone by is an error of TWO weights, decided by the caller.
 *
 * Blocking while the cycle is being built, or once a launched cycle has been edited: the aim is to
 * never launch a cycle over a date that has already passed, and a launched cycle's edited dates
 * must all be ahead again before they are saved. A warning only while a launched cycle is merely
 * consulted: its dates have naturally gone past and nothing is being written, so refusing the step
 * would lock the administrator out of the steps that follow.
 */
const getDeadlineFieldErrors = ({
    visibleMilestones,
    values,
    isPastDateBlocking = false,
    now = new Date(),
}: {
    visibleMilestones: readonly DeadlineMilestoneDefinition[];
    values: DeadlineMilestoneValues;
    isPastDateBlocking?: boolean;
    now?: Date;
}): DeadlineErrorsById => {
    const errors: DeadlineErrorsById = {};
    const pastDateSeverity: DeadlineFieldError['severity'] = isPastDateBlocking
        ? 'blocking'
        : 'warning';

    const setError = ({
        id,
        field,
        error,
    }: {
        id: DeadlineMilestoneIdValue;
        field: keyof DeadlineMilestoneErrors;
        error: DeadlineFieldError;
    }) => {
        const existing = errors[id]?.[field];

        if (
            existing &&
            DEADLINE_FIELD_SEVERITY_RANK[existing.severity] >
                DEADLINE_FIELD_SEVERITY_RANK[error.severity]
        ) {
            return;
        }

        errors[id] = { ...errors[id], [field]: error };
    };

    const closure = visibleMilestones.find(
        (milestone) => milestone.id === DeadlineMilestoneId.EVALUATION_CYCLE_CLOSURE,
    );
    const closureDate = closure ? values[closure.id]?.scheduledAt : undefined;

    for (const milestone of visibleMilestones) {
        const value = values[milestone.id];

        if (value?.scheduledAt && isDateInPast({ date: value.scheduledAt, now })) {
            setError({
                id: milestone.id,
                field: 'scheduledAt',
                error: {
                    key: 'cycleEvaluationDeadlines.validation.pastDate',
                    severity: pastDateSeverity,
                },
            });
        }

        if (isLaunchMilestone(milestone) && value?.dueAt) {
            if (isDateInPast({ date: value.dueAt, now })) {
                setError({
                    id: milestone.id,
                    field: 'dueAt',
                    error: {
                        key: 'cycleEvaluationDeadlines.validation.pastDate',
                        severity: pastDateSeverity,
                    },
                });
            }

            if (value.scheduledAt && value.dueAt.getTime() <= value.scheduledAt.getTime()) {
                setError({
                    id: milestone.id,
                    field: 'dueAt',
                    error: {
                        key: 'cycleEvaluationDeadlines.validation.dueBeforeStart',
                        severity: 'blocking',
                    },
                });
            }

            if (
                value.scheduledAt &&
                daysBetween({ start: value.scheduledAt, end: value.dueAt }) >
                    MAX_DAYS_ALLOWED_TO_COMPLETE
            ) {
                setError({
                    id: milestone.id,
                    field: 'dueAt',
                    error: {
                        key: 'evaluation360ScheduleCreation.validation.daysMax',
                        severity: 'blocking',
                    },
                });
            }
        }

        if (closure && milestone.id !== closure.id && closureDate) {
            const otherDate = getEffectiveLastDate({ milestone, value });

            if (otherDate && otherDate.getTime() > closureDate.getTime()) {
                setError({
                    id: closure.id,
                    field: 'scheduledAt',
                    error: {
                        key: 'cycleEvaluationDeadlines.validation.closureNotLast',
                        severity: 'blocking',
                    },
                });
            }
        }
    }

    return errors;
};

const hasBlockingDeadlineError = (errors: DeadlineErrorsById): boolean =>
    Object.values(errors).some(
        (milestoneErrors) =>
            milestoneErrors?.scheduledAt?.severity === 'blocking' ||
            milestoneErrors?.dueAt?.severity === 'blocking',
    );

const getRecurrenceError = (frequency?: FrequencyValue): DeadlineFieldError | undefined =>
    frequency
        ? undefined
        : {
              key: 'cycleEvaluationSchedule.validation.feedbackFrequencyRequired',
              severity: 'blocking',
          };

const getDeadlinesBlockingMessage = ({
    errors,
    frequency,
}: {
    errors: DeadlineErrorsById;
    frequency?: FrequencyValue;
}): TranslationKey | undefined => {
    const recurrenceError = getRecurrenceError(frequency);

    if (recurrenceError) {
        return recurrenceError.key;
    }

    return hasBlockingDeadlineError(errors)
        ? 'cycleEvaluationDeadlines.validation.stepBlocked'
        : undefined;
};

export {
    isDateInPast,
    getDeadlineFieldErrors,
    hasBlockingDeadlineError,
    getRecurrenceError,
    getDeadlinesBlockingMessage,
};
